import { useRef, useState } from 'react';
import { Button, Dropdown, Modal, message } from 'antd';
import { DownOutlined, ReloadOutlined } from '@ant-design/icons';
import { refreshUserRenewalStatus } from '../../api/community';
import { PermissionControl, usePermissions } from '../../permissions/access';
import { showRequestError } from '../../utils/request-feedback';

export const renewalRefreshPermission = 'alluser.control.refreshRenewal';

export default function UserRenewalRefresh({ selectedRows = [], onQueued }) {
    const [loading, setLoading] = useState(false);
    const pending = useRef(false);
    const confirming = useRef(false);
    const { can } = usePermissions();
    const selectedIds = [...new Set(selectedRows.map(row => row.userId).filter(Boolean))];

    const submit = async (allUsers, userIds) => {
        if (pending.current || !can(renewalRefreshPermission)) return;
        pending.current = true;
        setLoading(true);
        try {
            const result = await refreshUserRenewalStatus({ allUsers, userIds });
            // The shared mutation helper already reports HTTP failures and returns false.
            if (result === false) return;
            message.success({
                content: allUsers
                    ? 'Refresh requested for all Pro users. Results will update in about 5 minutes.'
                    : `Refresh requested for ${userIds.length} selected user${userIds.length === 1 ? '' : 's'}. Results will update shortly.`,
                duration: 5,
            });
            onQueued?.({ allUsers });
        } catch (error) {
            showRequestError(error);
        } finally {
            pending.current = false;
            setLoading(false);
        }
    };

    const chooseScope = ({ key }) => {
        if (pending.current || confirming.current || !can(renewalRefreshPermission)) return;
        if (key === 'selected') {
            if (selectedIds.length > 0) submit(false, selectedIds);
            return;
        }
        if (key !== 'all') return;
        confirming.current = true;
        Modal.confirm({
            title: 'Refresh all Pro users?',
            content: 'This checks every Pro user, including users outside the current page and filters. Results will update in about 5 minutes.',
            okText: 'Refresh all',
            cancelText: 'Cancel',
            onOk: () => submit(true, []),
            afterClose: () => { confirming.current = false; },
        });
    };

    return (
        <PermissionControl permission="alluser.control.refreshRenewal">
            <Dropdown trigger={['click']} disabled={loading} menu={{
                onClick: chooseScope,
                items: [
                    { key: 'selected', label: `Refresh selected users (${selectedIds.length})`, disabled: selectedIds.length === 0 },
                    { key: 'all', label: 'Refresh all Pro users' },
                ],
            }}>
                <Button loading={loading} disabled={loading} icon={<ReloadOutlined />}>Refresh Renew Status <DownOutlined /></Button>
            </Dropdown>
        </PermissionControl>
    );
}
